import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/lib/store'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Toast } from '@/components/ui/toast'
import {
  Package,
  User,
  MapPin,
  Clock,
  CheckCircle,
  XCircle,
  Truck,
  ArrowRight,
  Star,
  DollarSign,
  Shield,
  AlertTriangle
} from 'lucide-react'

interface DeliveryRequest {
  id: string
  order_id: string
  seller_id: string
  current_step: string
  status: string
  pickup_instructions: string | null
  delivery_notes: string | null
  created_at: string
  verification_status: string | null
  verification_code: string | null
  order: {
    id: string
    buyer_id: string
    final_price: number
    delivery_address: string
    payment_type: string
    confirmation_code: string | null
    product: {
      title: string
      images: string[]
    }
    buyer: {
      full_name: string | null
      phone_number: string | null
    }
    seller: {
      full_name: string | null
    }
  }
}

export default function DeliveryDashboard() {
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const [activeTab, setActiveTab] = useState<'available' | 'active' | 'completed'>('available')
  const [deliveries, setDeliveries] = useState<DeliveryRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')
  const [selectedDelivery, setSelectedDelivery] = useState<DeliveryRequest | null>(null)
  const [showDeliveryDetails, setShowDeliveryDetails] = useState(false)
  const [confirmationCode, setConfirmationCode] = useState('')
  const [verifying, setVerifying] = useState(false)

  useEffect(() => {
    if (!user) {
      navigate('/login')
      return
    }

    fetchDeliveries()
  }, [user, navigate, activeTab])

  const fetchDeliveries = async () => {
    if (!user) return

    try {
      setLoading(true)
      let query = supabase
        .from('delivery_requests')
        .select(`
          *,
          order:orders (
            id,
            buyer_id,
            final_price,
            delivery_address,
            payment_type,
            confirmation_code,
            product:products (
              title,
              images
            ),
            buyer:profiles!buyer_id (
              full_name,
              phone_number
            ),
            seller:profiles!seller_id (
              full_name
            )
          )
        `)

      if (activeTab === 'available') {
        // Available deliveries (not assigned to anyone)
        query = query
          .is('delivery_partner_id', null)
          .eq('status', 'pending')
      } else if (activeTab === 'active') {
        // Active deliveries (assigned to this delivery partner and not completed)
        query = query
          .eq('delivery_partner_id', user.id)
          .not('current_step', 'in', ['delivered', 'cancelled'])
      } else {
        // Completed deliveries
        query = query
          .eq('delivery_partner_id', user.id)
          .in('current_step', ['delivered', 'cancelled'])
      }

      query = query.order('created_at', { ascending: false })

      const { data, error } = await query
      if (error) throw error

      setDeliveries(data || [])
    } catch (err) {
      console.error('Error fetching deliveries:', err)
    } finally {
      setLoading(false)
    }
  }

  const acceptDelivery = async (deliveryId: string) => {
    try {
      const { error } = await supabase
        .from('delivery_requests')
        .update({
          delivery_partner_id: user?.id,
          status: 'accepted',
          current_step: 'accepted',
          step_metadata: {
            accepted_at: new Date().toISOString(),
            accepted_by: user?.id
          }
        })
        .eq('id', deliveryId)

      if (error) throw error

      setToastMessage('Livraison acceptée avec succès')
      setToastType('success')
      setShowToast(true)
      fetchDeliveries()
    } catch (err) {
      console.error('Error accepting delivery:', err)
      setToastMessage('Erreur lors de l\'acceptation de la livraison')
      setToastType('error')
      setShowToast(true)
    }
  }

  const updateDeliveryStep = async (deliveryId: string, step: string) => {
    try {
      const { error } = await supabase
        .from('delivery_requests')
        .update({
          current_step: step,
          step_metadata: {
            updated_at: new Date().toISOString(),
            updated_by: user?.id,
            step
          }
        })
        .eq('id', deliveryId)

      if (error) throw error

      // If the step is delivered, also update the order status
      if (step === 'delivered') {
        const delivery = deliveries.find(d => d.id === deliveryId)
        if (delivery) {
          await supabase
            .from('orders')
            .update({ status: 'delivered' })
            .eq('id', delivery.order_id)
        }
      }

      setToastMessage('Étape mise à jour avec succès')
      setToastType('success')
      setShowToast(true)
      setShowDeliveryDetails(false)
      fetchDeliveries()
    } catch (err) {
      console.error('Error updating delivery step:', err)
      setToastMessage('Erreur lors de la mise à jour de l\'étape')
      setToastType('error')
      setShowToast(true)
    }
  }

  const verifyConfirmationCode = async () => {
    if (!selectedDelivery || !confirmationCode) return
    
    try {
      setVerifying(true)
      
      // Check if the confirmation code matches
      if (selectedDelivery.order.payment_type === 'in_person' && 
          selectedDelivery.order.confirmation_code === confirmationCode) {
        
        // Update delivery request
        const { error: updateError } = await supabase
          .from('delivery_requests')
          .update({
            current_step: 'delivered',
            verification_status: 'verified',
            verification_code: confirmationCode,
            step_metadata: {
              verified_at: new Date().toISOString(),
              verification_code: confirmationCode
            }
          })
          .eq('id', selectedDelivery.id)
          
        if (updateError) throw updateError
        
        // Update order status
        const { error: orderError } = await supabase
          .from('orders')
          .update({ 
            status: 'delivered'
          })
          .eq('id', selectedDelivery.order_id)
          
        if (orderError) throw orderError
        
        setToastMessage('Code confirmé! Livraison complétée avec succès')
        setToastType('success')
        setShowToast(true)
        setShowDeliveryDetails(false)
        fetchDeliveries()
      } else {
        // Update verification status to failed
        await supabase
          .from('delivery_requests')
          .update({
            verification_status: 'failed',
            verification_code: confirmationCode,
            step_metadata: {
              failed_at: new Date().toISOString(),
              verification_code: confirmationCode
            }
          })
          .eq('id', selectedDelivery.id)
          
        setToastMessage('Code de confirmation incorrect')
        setToastType('error')
        setShowToast(true)
      }
    } catch (err) {
      console.error('Error verifying confirmation code:', err)
      setToastMessage('Erreur lors de la vérification du code')
      setToastType('error')
      setShowToast(true)
    } finally {
      setVerifying(false)
      setConfirmationCode('')
    }
  }

  const getStepLabel = (step: string) => {
    switch (step) {
      case 'pending': return 'En attente'
      case 'accepted': return 'Acceptée'
      case 'preparing': return 'En préparation'
      case 'pickup_ready': return 'Prêt pour récupération'
      case 'picked_up': return 'Récupéré'
      case 'in_transit': return 'En transit'
      case 'arrived': return 'Arrivé'
      case 'delivered': return 'Livré'
      case 'cancelled': return 'Annulé'
      default: return step
    }
  }

  const getStepIcon = (step: string) => {
    switch (step) {
      case 'pending': return <Clock className="h-5 w-5 text-yellow-500" />
      case 'accepted': return <CheckCircle className="h-5 w-5 text-blue-500" />
      case 'preparing': return <Package className="h-5 w-5 text-purple-500" />
      case 'pickup_ready': return <Package className="h-5 w-5 text-indigo-500" />
      case 'picked_up': return <Truck className="h-5 w-5 text-orange-500" />
      case 'in_transit': return <Truck className="h-5 w-5 text-blue-500" />
      case 'arrived': return <MapPin className="h-5 w-5 text-green-500" />
      case 'delivered': return <CheckCircle className="h-5 w-5 text-green-500" />
      case 'cancelled': return <XCircle className="h-5 w-5 text-red-500" />
      default: return <Clock className="h-5 w-5 text-gray-500" />
    }
  }

  const getNextStep = (currentStep: string) => {
    const steps = [
      'pending',
      'accepted',
      'preparing',
      'pickup_ready',
      'picked_up',
      'in_transit',
      'arrived',
      'delivered'
    ]
    
    const currentIndex = steps.indexOf(currentStep)
    if (currentIndex === -1 || currentIndex === steps.length - 1) return null
    
    return steps[currentIndex + 1]
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      {showToast && (
        <Toast
          type={toastType}
          title={toastMessage}
          onClose={() => setShowToast(false)}
        />
      )}

      {/* Delivery Details Modal */}
      {showDeliveryDetails && selectedDelivery && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto m-4">
            <div className="p-6">
              <div className="flex justify-between items-start">
                <h3 className="text-lg font-medium text-gray-900">
                  Détails de la Livraison
                </h3>
                <button
                  onClick={() => setShowDeliveryDetails(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>

              <div className="mt-6 space-y-6">
                {/* Order Info */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Informations de la Commande</h4>
                  <div className="mt-2 flex items-center">
                    <div className="h-16 w-16 flex-shrink-0 bg-gray-100 rounded overflow-hidden">
                      <img
                        src={selectedDelivery.order.product?.images?.[0] || "https://images.unsplash.com/photo-1523275335684-37898b6baf30"}
                        alt={selectedDelivery.order.product?.title}
                        className="h-full w-full object-cover"
                      />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-sm font-medium text-gray-900">
                        {selectedDelivery.order.product?.title}
                      </h3>
                      <div className="mt-1 flex items-center text-sm text-gray-500">
                        <DollarSign className="h-4 w-4 mr-1" />
                        {selectedDelivery.order.final_price.toFixed(2)}
                      </div>
                      
                      {/* Payment Type Badge */}
                      {selectedDelivery.order.payment_type === 'in_person' && (
                        <div className="mt-1 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          <DollarSign className="h-3 w-3 mr-1" />
                          Paiement à la livraison
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Seller Info */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Vendeur</h4>
                  <div className="mt-2">
                    <p className="text-sm font-medium text-gray-900">
                      {selectedDelivery.order.seller?.full_name || 'Vendeur'}
                    </p>
                    <div className="mt-1 text-sm text-gray-500">
                      {selectedDelivery.pickup_instructions}
                    </div>
                  </div>
                </div>

                {/* Buyer Info */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Client</h4>
                  <div className="mt-2">
                    <p className="text-sm font-medium text-gray-900">
                      {selectedDelivery.order.buyer?.full_name || 'Client'}
                    </p>
                    <p className="text-sm text-gray-500">
                      {selectedDelivery.order.buyer?.phone_number}
                    </p>
                    <div className="mt-1 text-sm text-gray-500">
                      <MapPin className="h-4 w-4 inline mr-1" />
                      {selectedDelivery.order.delivery_address}
                    </div>
                    <div className="mt-1 text-sm text-gray-500">
                      {selectedDelivery.delivery_notes}
                    </div>
                  </div>
                </div>

                {/* Current Status */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Statut Actuel</h4>
                  <div className="mt-2 flex items-center">
                    {getStepIcon(selectedDelivery.current_step)}
                    <span className="ml-2 text-sm font-medium text-gray-900">
                      {getStepLabel(selectedDelivery.current_step)}
                    </span>
                  </div>
                </div>

                {/* In-Person Payment Verification */}
                {selectedDelivery.order.payment_type === 'in_person' && 
                 selectedDelivery.current_step === 'arrived' && (
                  <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                    <h4 className="text-sm font-medium text-yellow-800 flex items-center">
                      <Shield className="h-4 w-4 mr-2" />
                      Vérification du paiement à la livraison
                    </h4>
                    <p className="mt-2 text-sm text-yellow-700">
                      Demandez au client de vous fournir le code de confirmation pour finaliser la livraison et confirmer le paiement.
                    </p>
                    
                    <div className="mt-3">
                      <label htmlFor="confirmationCode" className="block text-sm font-medium text-yellow-800">
                        Code de confirmation
                      </label>
                      <div className="mt-1 flex space-x-2">
                        <input
                          type="text"
                          id="confirmationCode"
                          value={confirmationCode}
                          onChange={(e) => setConfirmationCode(e.target.value.toUpperCase())}
                          placeholder="Entrez le code à 6 caractères"
                          className="block w-full border-yellow-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                          maxLength={6}
                        />
                        <Button
                          variant="primary"
                          onClick={verifyConfirmationCode}
                          disabled={!confirmationCode || verifying}
                        >
                          {verifying ? 'Vérification...' : 'Vérifier'}
                        </Button>
                      </div>
                      
                      {selectedDelivery.verification_status === 'failed' && (
                        <p className="mt-2 text-sm text-red-600 flex items-center">
                          <AlertTriangle className="h-4 w-4 mr-1" />
                          Code incorrect. Veuillez réessayer.
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* Next Step Button */}
                <div className="pt-6 border-t flex justify-end space-x-3">
                  <Button
                    variant="secondary"
                    onClick={() => setShowDeliveryDetails(false)}
                  >
                    Fermer
                  </Button>
                  
                  {/* Only show next step button if not waiting for confirmation code verification */}
                  {!(selectedDelivery.order.payment_type === 'in_person' && 
                     selectedDelivery.current_step === 'arrived') && 
                   getNextStep(selectedDelivery.current_step) && (
                    <Button
                      variant="primary"
                      onClick={() => updateDeliveryStep(
                        selectedDelivery.id, 
                        getNextStep(selectedDelivery.current_step) || ''
                      )}
                    >
                      {getStepLabel(getNextStep(selectedDelivery.current_step) || '')}
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">
            Tableau de Bord Livreur
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Gérez vos livraisons et suivez votre activité
          </p>
        </div>

        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex">
              <button
                onClick={() => setActiveTab('available')}
                className={`${
                  activeTab === 'available'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } flex-1 whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm text-center`}
              >
                Disponibles
              </button>
              <button
                onClick={() => setActiveTab('active')}
                className={`${
                  activeTab === 'active'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } flex-1 whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm text-center`}
              >
                En cours
              </button>
              <button
                onClick={() => setActiveTab('completed')}
                className={`${
                  activeTab === 'completed'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } flex-1 whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm text-center`}
              >
                Terminées
              </button>
            </nav>
          </div>

          <div className="p-4 sm:p-6">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : deliveries.length === 0 ? (
              <div className="text-center py-12">
                <Truck className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  {activeTab === 'available'
                    ? 'Aucune livraison disponible'
                    : activeTab === 'active'
                    ? 'Aucune livraison en cours'
                    : 'Aucune livraison terminée'}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {activeTab === 'available'
                    ? 'Revenez plus tard pour voir les nouvelles livraisons disponibles'
                    : activeTab === 'active'
                    ? 'Acceptez des livraisons pour les voir ici'
                    : 'Vos livraisons terminées apparaîtront ici'}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {deliveries.map((delivery) => (
                  <div
                    key={delivery.id}
                    className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow"
                  >
                    <div className="p-4 border-b border-gray-200 bg-gray-50">
                      <div className="flex justify-between items-start">
                        <div className="flex items-center">
                          {getStepIcon(delivery.current_step)}
                          <span className="ml-2 text-sm font-medium text-gray-900">
                            {getStepLabel(delivery.current_step)}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500">
                          {new Date(delivery.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>

                    <div className="p-4">
                      <div className="flex items-start">
                        <div className="h-12 w-12 flex-shrink-0 bg-gray-100 rounded overflow-hidden">
                          <img
                            src={delivery.order.product?.images?.[0] || "https://images.unsplash.com/photo-1523275335684-37898b6baf30"}
                            alt={delivery.order.product?.title}
                            className="h-full w-full object-cover"
                          />
                        </div>
                        <div className="ml-3 flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {delivery.order.product?.title}
                          </p>
                          <div className="mt-1 flex items-center text-sm text-gray-500">
                            <DollarSign className="h-4 w-4 mr-1 flex-shrink-0" />
                            {delivery.order.final_price.toFixed(2)}
                            
                            {/* Payment Type Badge */}
                            {delivery.order.payment_type === 'in_person' && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                Paiement à la livraison
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="mt-4 space-y-2">
                        <div className="flex items-start">
                          <MapPin className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                          <span className="ml-2 text-sm text-gray-500 line-clamp-2">
                            {delivery.order.delivery_address}
                          </span>
                        </div>
                        <div className="flex items-center">
                          <User className="h-5 w-5 text-gray-400 flex-shrink-0" />
                          <span className="ml-2 text-sm text-gray-500">
                            {delivery.order.buyer?.full_name || 'Client'}
                          </span>
                        </div>
                      </div>

                      <div className="mt-4 flex justify-end">
                        {activeTab === 'available' ? (
                          <Button
                            variant="primary"
                            onClick={() => acceptDelivery(delivery.id)}
                          >
                            Accepter la livraison
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            onClick={() => {
                              setSelectedDelivery(delivery)
                              setShowDeliveryDetails(true)
                            }}
                          >
                            Voir les détails
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}